import React, { useState, useEffect, useRef } from 'react';
import { dryRunTests, dryRunWithScript, getQuestions, getCategories, getTestCasesForQuestion } from '../../lib/adminApi';

function AdminDryRun() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]); // Multiple questions selection
  const [selectedCategory, setSelectedCategory] = useState('');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Test case selection
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [loadingTestCases, setLoadingTestCases] = useState(false);

  // Script editing state
  const [editingScripts, setEditingScripts] = useState({}); // { questionId: scriptText }
  const [editingTestCases, setEditingTestCases] = useState({}); // { questionId: [testCases] }
  const [rerunningQuestion, setRerunningQuestion] = useState(null); // questionId being re-run

  // Refs to prevent duplicate fetches
  const hasFetchedCategories = useRef(false);

  // Fetch categories only once on mount
  useEffect(() => {
    if (hasFetchedCategories.current) return;
    hasFetchedCategories.current = true;

    const fetchCategories = async () => {
      try {
        const cData = await getCategories();
        setCategories(cData.categories || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch questions when category is selected
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedCategory) {
        setQuestions([]);
        return;
      }

      setLoadingQuestions(true);
      try {
        const qData = await getQuestions(1, 20, selectedCategory);
        setQuestions(qData.questions || []);
      } catch (err) {
        console.error('Failed to fetch questions:', err);
        setQuestions([]);
      } finally {
        setLoadingQuestions(false);
      }
    };

    setSelectedQuestions([]);
    setTestCases([]);
    setSelectedTestCases([]);
    fetchQuestions();
  }, [selectedCategory]);

  // Load test cases when a single question is selected
  useEffect(() => {
    const loadTestCases = async () => {
      if (selectedQuestions.length === 1) {
        setLoadingTestCases(true);
        try {
          const data = await getTestCasesForQuestion(selectedQuestions[0]);
          setTestCases(data.testCases || []);
          setSelectedTestCases([]); // Reset selection
        } catch (err) {
          console.error('Failed to load test cases:', err);
          setTestCases([]);
        } finally {
          setLoadingTestCases(false);
        }
      } else {
        setTestCases([]);
        setSelectedTestCases([]);
      }
    };
    loadTestCases();
  }, [selectedQuestions]);

  const handleRunTests = async () => {
    setLoading(true);
    setResults(null);
    try {
      // If multiple questions selected, pass first one (API supports single question)
      // If no questions selected but category is, pass category for filtering
      const questionId = selectedQuestions.length === 1 ? selectedQuestions[0] : null;
      const categoryId = selectedCategory || null;
      const testCaseIds = selectedTestCases.length > 0 ? selectedTestCases : null;

      // If multiple questions selected, we need to run for each or pass question IDs
      // For now, run with the selected question IDs array
      const questionIds = selectedQuestions.length > 0 ? selectedQuestions : null;

      const data = await dryRunTests(questionId, categoryId, testCaseIds, questionIds);
      setResults(data);
    } catch (err) {
      console.error('Failed to run tests:', err);
      setResults({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionToggle = (questionId) => {
    setSelectedQuestions(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleSelectAllQuestions = () => {
    if (selectedQuestions.length === questions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(questions.map(q => q.questionId));
    }
  };

  const handleTestCaseToggle = (testCaseId) => {
    setSelectedTestCases(prev =>
      prev.includes(testCaseId)
        ? prev.filter(id => id !== testCaseId)
        : [...prev, testCaseId]
    );
  };

  const handleSelectAllTestCases = () => {
    if (selectedTestCases.length === testCases.length) {
      setSelectedTestCases([]);
    } else {
      setSelectedTestCases(testCases.map(tc => tc.testCaseId));
    }
  };

  // Handle script editing
  const handleScriptChange = (questionId, newScript) => {
    setEditingScripts(prev => ({
      ...prev,
      [questionId]: newScript
    }));
  };

  // Handle test case editing
  const handleTestCaseChange = (questionId, testCaseIndex, field, value) => {
    setEditingTestCases(prev => {
      const currentTestCases = prev[questionId] || [];
      const updatedTestCases = [...currentTestCases];
      updatedTestCases[testCaseIndex] = {
        ...updatedTestCases[testCaseIndex],
        [field]: value
      };
      return {
        ...prev,
        [questionId]: updatedTestCases
      };
    });
  };

  // Initialize test cases for editing from results
  const initializeTestCasesForEditing = (questionId, testResults) => {
    if (!editingTestCases[questionId]) {
      setEditingTestCases(prev => ({
        ...prev,
        [questionId]: testResults.map(tc => ({
          testCaseId: tc.testCaseId,
          input: tc.input || '',
          expectedOutput: tc.expectedOutput || '',
          outputType: tc.outputType || 'string',
          hidden: tc.hidden || false
        }))
      }));
    }
  };

  // Reset test cases to original
  const resetTestCases = (questionId, testResults) => {
    setEditingTestCases(prev => ({
      ...prev,
      [questionId]: testResults.map(tc => ({
        testCaseId: tc.testCaseId,
        input: tc.input || '',
        expectedOutput: tc.expectedOutput || '',
        outputType: tc.outputType || 'string',
        hidden: tc.hidden || false
      }))
    }));
  };

  // Add new test case for editing
  const addTestCaseForEditing = (questionId) => {
    setEditingTestCases(prev => {
      const currentTestCases = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: [
          ...currentTestCases,
          {
            testCaseId: Date.now(), // temporary ID
            input: '',
            expectedOutput: '',
            outputType: 'string',
            hidden: false
          }
        ]
      };
    });
  };

  // Remove test case from editing
  const removeTestCaseForEditing = (questionId, testCaseIndex) => {
    setEditingTestCases(prev => {
      const currentTestCases = prev[questionId] || [];
      return {
        ...prev,
        [questionId]: currentTestCases.filter((_, idx) => idx !== testCaseIndex)
      };
    });
  };

  // Re-run dry run with modified script and/or test cases
  const handleRerunWithScript = async (questionId) => {
    const script = editingScripts[questionId];
    const customTestCases = editingTestCases[questionId];

    if ((!script || !script.trim()) && (!customTestCases || customTestCases.length === 0)) {
      alert('Please modify the script or test cases before re-running');
      return;
    }

    setRerunningQuestion(questionId);
    try {
      const data = await dryRunWithScript(questionId, script, null, customTestCases);

      // Update results for this specific question
      if (data.questions && data.questions.length > 0) {
        const newQuestionResult = data.questions[0];
        setResults(prevResults => {
          if (!prevResults) return data;

          // Replace the question result in the existing results
          const updatedQuestions = prevResults.questions.map(q =>
            q.questionId === questionId ? newQuestionResult : q
          );

          // Recalculate totals
          let totalPassed = 0;
          let totalFailed = 0;
          let totalTests = 0;
          updatedQuestions.forEach(q => {
            totalPassed += q.passed || 0;
            totalFailed += q.failed || 0;
            totalTests += q.total || 0;
          });

          return {
            ...prevResults,
            questions: updatedQuestions,
            totalPassed,
            totalFailed,
            totalTests,
            success: totalFailed === 0
          };
        });

        // Update editing test cases with new results
        if (newQuestionResult.testResults) {
          resetTestCases(questionId, newQuestionResult.testResults);
        }
      }
    } catch (err) {
      console.error('Failed to re-run with script:', err);
      alert('Failed to re-run: ' + err.message);
    } finally {
      setRerunningQuestion(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PASSED: 'bg-green-600/20 text-green-400',
      FAILED: 'bg-red-600/20 text-red-400',
      NO_TESTS: 'bg-yellow-600/20 text-yellow-400',
      NO_ANSWER: 'bg-orange-600/20 text-orange-400',
      NO_TEMPLATE: 'bg-orange-600/20 text-orange-400',
    };
    return styles[status] || 'bg-gray-600/20 text-gray-400';
  };

  // Calculate category-wise statistics from results
  const getCategoryStats = () => {
    if (!results?.questions) return [];

    const categoryMap = {};

    results.questions.forEach((q) => {
      const categoryName = q.categoryName || 'Uncategorized';
      const categoryId = q.categoryId || 'uncategorized';

      if (!categoryMap[categoryId]) {
        categoryMap[categoryId] = {
          categoryId,
          categoryName,
          totalTests: 0,
          passed: 0,
          failed: 0,
          questions: 0,
        };
      }

      categoryMap[categoryId].totalTests += q.total || 0;
      categoryMap[categoryId].passed += q.passed || 0;
      categoryMap[categoryId].failed += q.failed || 0;
      categoryMap[categoryId].questions += 1;
    });

    return Object.values(categoryMap).sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Dry Run - Test Validation</h1>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 mb-6">
        <h2 className="text-lg font-semibold text-white mb-3">Run Test Validation</h2>
        <p className="text-gray-400 text-sm mb-4">
          Validate that all test cases work correctly. Select a question and optionally specific test cases to run.
        </p>

        {/* Step 1: Select Category */}
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-1">Step 1: Select Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full max-w-md px-3 py-2 bg-gray-700 text-white rounded border border-gray-600"
          >
            <option value="">All Categories (Run all questions)</option>
            {categories.map((c) => (
              <option key={c.categoryId} value={c.categoryId}>
                {c.categoryName}
              </option>
            ))}
          </select>
        </div>

        {/* Step 2: Select Questions (when category is selected) */}
        {selectedCategory && (
          <div className="mb-4 p-3 bg-gray-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-300 font-medium">
                Step 2: Select Questions {!loadingQuestions && `(${questions.length} available)`}
              </label>
              {!loadingQuestions && questions.length > 0 && (
                <button
                  onClick={handleSelectAllQuestions}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  {selectedQuestions.length === questions.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
            {loadingQuestions ? (
              <div className="text-gray-400 text-sm">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="text-gray-500 text-sm">No questions found in this category</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {questions.map((q) => (
                  <label
                    key={q.questionId}
                    className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-colors ${
                      selectedQuestions.includes(q.questionId)
                        ? 'bg-purple-600/30 border border-purple-500'
                        : 'bg-gray-600/50 border border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedQuestions.includes(q.questionId)}
                      onChange={() => handleQuestionToggle(q.questionId)}
                      className="mt-1 w-4 h-4 accent-purple-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-purple-300">Q#{q.questionId}</span>
                        {q.testCaseCount !== undefined && (
                          <span className="text-xs text-gray-500">({q.testCaseCount} test cases)</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-300 mt-1 line-clamp-2">
                        {q.question || 'No question text'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {selectedQuestions.length > 0 && (
              <div className="mt-2 text-xs text-gray-400">
                {selectedQuestions.length} question(s) selected
              </div>
            )}
            {selectedQuestions.length === 0 && questions.length > 0 && !loadingQuestions && (
              <div className="mt-2 text-xs text-gray-500">
                No selection = run all questions in category
              </div>
            )}
          </div>
        )}

        {/* Step 3: Select Test Cases (when single question is selected) */}
        {selectedQuestions.length === 1 && (
          <div className="mb-4 p-3 bg-gray-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-300 font-medium">Step 3: Select Test Cases</label>
              {testCases.length > 0 && (
                <button
                  onClick={handleSelectAllTestCases}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  {selectedTestCases.length === testCases.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
            {loadingTestCases ? (
              <div className="text-gray-400 text-sm">Loading test cases...</div>
            ) : testCases.length === 0 ? (
              <div className="text-gray-500 text-sm">No test cases found for this question</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {testCases.map((tc) => (
                  <label
                    key={tc.testCaseId}
                    className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-colors ${
                      selectedTestCases.includes(tc.testCaseId)
                        ? 'bg-blue-600/30 border border-blue-500'
                        : 'bg-gray-600/50 border border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTestCases.includes(tc.testCaseId)}
                      onChange={() => handleTestCaseToggle(tc.testCaseId)}
                      className="mt-1 w-4 h-4 accent-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-blue-300">TC#{tc.testCaseId}</span>
                        {tc.hidden && <span className="px-1.5 py-0.5 bg-yellow-600/20 text-yellow-400 text-xs rounded">Hidden</span>}
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Input:</span>
                          <code className="ml-1 text-gray-300 break-all">{tc.inputValue || tc.input || 'N/A'}</code>
                        </div>
                        <div>
                          <span className="text-gray-500">Output:</span>
                          <code className="ml-1 text-gray-300 break-all">{tc.outputValue || tc.output || 'N/A'}</code>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {selectedTestCases.length > 0 && (
              <div className="mt-2 text-xs text-gray-400">
                {selectedTestCases.length} test case(s) selected
              </div>
            )}
            {selectedTestCases.length === 0 && testCases.length > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                No selection = run all test cases
              </div>
            )}
          </div>
        )}

        {/* Info when multiple questions selected */}
        {selectedQuestions.length > 1 && (
          <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-700/30 rounded-lg">
            <p className="text-yellow-400 text-sm">
              Note: Test case selection is only available when a single question is selected.
              With multiple questions, all test cases will be run for each selected question.
            </p>
          </div>
        )}

        <button
          onClick={handleRunTests}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Running Tests...
            </>
          ) : (
            <>🔬 Run Dry Run</>
          )}
        </button>
      </div>

      {/* Results */}
      {results && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          {/* Summary */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex flex-wrap gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{results.totalTests || 0}</div>
                <div className="text-sm text-gray-400">Total Tests</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400">{results.totalPassed || 0}</div>
                <div className="text-sm text-gray-400">Passed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-400">{results.totalFailed || 0}</div>
                <div className="text-sm text-gray-400">Failed</div>
              </div>
              <div className="flex-1 flex items-center justify-end">
                <span className={`px-4 py-2 rounded-lg font-semibold ${results.success ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                  {results.success ? '✓ All Tests Passed' : '✗ Some Tests Failed'}
                </span>
              </div>
            </div>
          </div>

          {/* Category-wise Statistics */}
          {getCategoryStats().length > 0 && (
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-sm font-semibold text-gray-400 uppercase mb-3">Category-wise Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {getCategoryStats().map((cat) => {
                  const passPercentage = cat.totalTests > 0 ? ((cat.passed / cat.totalTests) * 100).toFixed(1) : 0;
                  const failPercentage = cat.totalTests > 0 ? ((cat.failed / cat.totalTests) * 100).toFixed(1) : 0;
                  const allPassed = cat.failed === 0 && cat.totalTests > 0;

                  return (
                    <div
                      key={cat.categoryId}
                      className={`p-3 rounded-lg border ${
                        allPassed
                          ? 'bg-green-900/10 border-green-700/30'
                          : cat.passed === 0 && cat.totalTests > 0
                            ? 'bg-red-900/10 border-red-700/30'
                            : 'bg-gray-700/30 border-gray-600/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium text-sm truncate" title={cat.categoryName}>
                          {cat.categoryName}
                        </span>
                        <span className="text-xs text-gray-500">
                          {cat.questions} Q{cat.questions > 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Progress Bar - only show if there are failures */}
                      {cat.failed > 0 && (
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
                          <div className="h-full flex">
                            <div
                              className="bg-green-500 transition-all duration-300"
                              style={{ width: `${passPercentage}%` }}
                            />
                            <div
                              className="bg-red-500 transition-all duration-300"
                              style={{ width: `${failPercentage}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Stats */}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3">
                          <span className="text-green-400">
                            ✓ {cat.passed} passed ({passPercentage}%)
                          </span>
                          {cat.failed > 0 && (
                            <span className="text-red-400">
                              ✗ {cat.failed} failed ({failPercentage}%)
                            </span>
                          )}
                        </div>
                        <span className="text-gray-500">
                          {cat.totalTests} tests
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Question Results */}
          <div className="divide-y divide-gray-700">
            {results.questions?.map((q) => (
              <div key={q.questionId} className="p-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedQuestion(expandedQuestion === q.questionId ? null : q.questionId)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-gray-400 shrink-0">Q#{q.questionId}</span>
                    <span className="text-white truncate">{q.questionPreview || q.question}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(q.status)}`}>
                      {q.status}
                    </span>
                    <span className="text-gray-400 text-sm">
                      {q.passed}/{q.total}
                    </span>
                    <span className="text-gray-500">
                      {expandedQuestion === q.questionId ? '▼' : '▶'}
                    </span>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedQuestion === q.questionId && (
                  <div className="mt-4 space-y-4">
                    {/* Full Question Text */}
                    {q.question && (
                      <div className="p-3 bg-gray-700/30 rounded-lg">
                        <div className="text-xs text-gray-500 uppercase mb-1">Full Question</div>
                        <div className="text-gray-200 text-sm whitespace-pre-wrap">{q.question}</div>
                      </div>
                    )}

                    {/* Script Editor - Modify and Re-run */}
                    <div className="p-3 bg-gray-700/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-gray-500 uppercase">Answer Script (Editable)</div>
                        <div className="flex items-center gap-2">
                          {editingScripts[q.questionId] !== undefined && editingScripts[q.questionId] !== q.script && (
                            <span className="text-xs text-yellow-400">Modified</span>
                          )}
                          <button
                            onClick={() => handleScriptChange(q.questionId, q.script || '')}
                            className="px-2 py-1 text-xs bg-gray-600 text-gray-300 rounded hover:bg-gray-500"
                            title="Reset to original script"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      <textarea
                        value={editingScripts[q.questionId] !== undefined ? editingScripts[q.questionId] : (q.script || '')}
                        onChange={(e) => handleScriptChange(q.questionId, e.target.value)}
                        className="w-full h-48 p-3 bg-gray-900 text-green-300 text-xs font-mono rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                        placeholder="Enter script to test..."
                        spellCheck={false}
                      />
                      <div className="mt-2 flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                          Modify the script above and click "Re-run" to test changes (not persisted)
                        </div>
                        <button
                          onClick={() => handleRerunWithScript(q.questionId)}
                          disabled={rerunningQuestion === q.questionId}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {rerunningQuestion === q.questionId ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              Re-running...
                            </>
                          ) : (
                            <>🔄 Re-run with Modifications</>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Editable Test Cases */}
                    {q.testResults && q.testResults.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500 uppercase">Test Cases (Editable)</div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => addTestCaseForEditing(q.questionId)}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              + Add Test Case
                            </button>
                            <button
                              onClick={() => resetTestCases(q.questionId, q.testResults)}
                              className="px-2 py-1 text-xs bg-gray-600 text-gray-300 rounded hover:bg-gray-500"
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                        {/* Initialize test cases for editing if not already done */}
                        {!editingTestCases[q.questionId] && initializeTestCasesForEditing(q.questionId, q.testResults)}

                        {(editingTestCases[q.questionId] || q.testResults).map((tc, idx) => {
                          const editTc = editingTestCases[q.questionId]?.[idx] || tc;
                          const originalTc = q.testResults[idx];
                          const isModified = editingTestCases[q.questionId] && (
                            editTc.input !== (originalTc?.input || '') ||
                            editTc.expectedOutput !== (originalTc?.expectedOutput || '')
                          );

                          return (
                            <div
                              key={tc.testCaseId || idx}
                              className={`p-3 rounded border ${
                                originalTc?.passed === undefined
                                  ? 'bg-gray-800/50 border-gray-700'
                                  : originalTc?.passed
                                    ? 'bg-green-900/20 border-green-800/30'
                                    : 'bg-red-900/20 border-red-800/30'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-400">
                                  Test Case #{idx + 1}
                                  {isModified && <span className="ml-2 px-1.5 py-0.5 bg-yellow-600/20 text-yellow-400 text-xs rounded">Modified</span>}
                                </span>
                                <div className="flex items-center gap-2">
                                  {originalTc?.passed !== undefined && (
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${originalTc?.passed ? 'bg-green-600/30 text-green-400' : 'bg-red-600/30 text-red-400'}`}>
                                      {originalTc?.passed ? '✓ PASS' : '✗ FAIL'}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => removeTestCaseForEditing(q.questionId, idx)}
                                    className="px-2 py-1 text-xs bg-red-600/50 text-red-300 rounded hover:bg-red-600"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                                <div>
                                  <label className="text-gray-500 text-xs block mb-1">Input (JSON)</label>
                                  <input
                                    type="text"
                                    value={editTc.input || ''}
                                    onChange={(e) => handleTestCaseChange(q.questionId, idx, 'input', e.target.value)}
                                    className="w-full px-2 py-1 bg-gray-900 text-gray-300 text-xs rounded border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder='{"param": "value"}'
                                  />
                                </div>
                                <div>
                                  <label className="text-gray-500 text-xs block mb-1">Expected Output</label>
                                  <input
                                    type="text"
                                    value={editTc.expectedOutput || ''}
                                    onChange={(e) => handleTestCaseChange(q.questionId, idx, 'expectedOutput', e.target.value)}
                                    className="w-full px-2 py-1 bg-gray-900 text-gray-300 text-xs rounded border border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Expected output"
                                  />
                                </div>
                              </div>

                              {/* Show actual output from last run */}
                              {originalTc?.actualOutput !== undefined && (
                                <div className="mt-2 p-2 bg-gray-800/50 rounded">
                                  <span className="text-gray-500 text-xs block mb-1">Actual Output (Last Run)</span>
                                  <code className={`text-xs break-all ${originalTc?.passed ? 'text-green-400' : 'text-red-400'}`}>
                                    {originalTc?.error ? `Error: ${originalTc.error}` : (originalTc?.actualOutput || 'N/A')}
                                  </code>
                                </div>
                              )}

                              {/* Function Response */}
                              {originalTc?.functionResponse && (
                                <details className="mt-2 group">
                                  <summary className="flex items-center gap-2 cursor-pointer text-xs text-gray-500 hover:text-gray-400">
                                    <span className="group-open:rotate-90 transition-transform">▶</span>
                                    View Function Response
                                  </summary>
                                  <pre className="mt-2 p-2 bg-gray-950 rounded text-xs text-cyan-300 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                                    {(() => {
                                      try {
                                        return JSON.stringify(JSON.parse(originalTc.functionResponse), null, 2);
                                      } catch {
                                        return originalTc.functionResponse;
                                      }
                                    })()}
                                  </pre>
                                </details>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* No test results message */}
                    {(!q.testResults || q.testResults.length === 0) && (
                      <div className="text-gray-500 text-sm italic">
                        {q.status === 'NO_TESTS' && 'No test cases defined for this question.'}
                        {q.status === 'NO_ANSWER' && 'No answer script defined for this question.'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDryRun;
